import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useDialog } from './useDialog';
import api from './api';
import { useWhatsApp } from '../context/WhatsAppContext';
import { Calendar, Views, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import useAuth from "../hooks/useAuth";
import { canDo } from '../utils/canDo';
import EscalationModal from './EscalationModal';
import './Doctors.css';

const DragAndDropCalendar = withDragAndDrop(Calendar);
const localizer = momentLocalizer(moment);

// Detect iOS 12 for compatibility fallback
const isiOS12 = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone/.test(ua) && /OS 12_\d+/.test(ua);
};

// ── Touch-to-mouse polyfill for DnD on mobile ──
// HTML5 drag events don't fire on touch; this translates touch → mouse events
// so react-big-calendar's DnD addon works on phones/tablets.
function addTouchDragSupport(container) {
  if (!container || !('ontouchstart' in window)) return () => {};
  let dragTarget = null;

  function fireMouse(type, touch) {
    const evt = new MouseEvent(type, {
      bubbles: true, cancelable: true,
      clientX: touch.clientX, clientY: touch.clientY,
      screenX: touch.screenX, screenY: touch.screenY,
    });
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    (el || container).dispatchEvent(evt);
  }

  function onTouchStart(e) {
    // Only intercept on calendar event elements (has rbc-event class)
    const ev = e.target.closest('.rbc-event');
    if (!ev) return;
    dragTarget = ev;
    const t = e.changedTouches[0];
    fireMouse('mousedown', t);
  }
  function onTouchMove(e) {
    if (!dragTarget) return;
    e.preventDefault();
    fireMouse('mousemove', e.changedTouches[0]);
  }
  function onTouchEnd(e) {
    if (!dragTarget) return;
    fireMouse('mouseup', e.changedTouches[0]);
    dragTarget = null;
  }

  container.addEventListener('touchstart', onTouchStart, { passive: true });
  container.addEventListener('touchmove',  onTouchMove,  { passive: false });
  container.addEventListener('touchend',   onTouchEnd,   { passive: true });
  return () => {
    container.removeEventListener('touchstart', onTouchStart);
    container.removeEventListener('touchmove',  onTouchMove);
    container.removeEventListener('touchend',   onTouchEnd);
  };
}

export default function Resource({ patientData }) {

  const [groupResourcesOnWeek, setGroupResourcesOnWeek] = useState(true);
  const [resources, setResources] = useState([]);
  const [events, setEvents] = useState([]);
  const [branches, setBranches] = useState([]);
  const [leaveforwhom, setLeaveForWhom] = useState("Self");

  // ── Multi-user checkbox picker state ──────────────────────────────────────
  const [allStaff,           setAllStaff]           = useState([]);  // all users fetched
  const [selectedUsernames,  setSelectedUsernames]  = useState(null); // null = not yet init
  const [showUserPicker,     setShowUserPicker]      = useState(false);
  const { auth } = useAuth();
  const { dialogNode, showAlert, showConfirm, showPrompt } = useDialog();
  const { sendWhatsApp } = useWhatsApp();
  const [myEvents, setMyEvents] = useState([]);
  const [checklistEvents, setChecklistEvents] = useState([]);
  var patientCard = patientData && patientData.patientCard;
  const patientName = patientData && patientData.name;
  const [newBranch, setNewBranch] = useState(patientData?.branch || null); // resolved on initial load
  const [currentView, setCurrentView] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calOnlineMode, setCalOnlineMode]   = useState(false);
  const [calDisplayView, setCalDisplayView] = useState('calendar');
  const [showTasks, setShowTasks]           = useState(false);  // hide checklist tasks by default
  const [detailEvent, setDetailEvent]       = useState(null);
  const [escalationEvent, setEscalationEvent] = useState(null); // for EscalationModal

  // ── Patient search modal state ──
  const [psOpen, setPsOpen]           = useState(false);
  const [psQuery, setPsQuery]         = useState('');
  const [psResults, setPsResults]     = useState([]);
  const [psLoading, setPsLoading]     = useState(false);
  const [psNewMode, setPsNewMode]     = useState(false);
  const [psNewForm, setPsNewForm]     = useState({ name: '', number: '', age: '', gender: 'male', purpose: '' });
  const [psCreating, setPsCreating]   = useState(false);
  const [pendingSlot, setPendingSlot] = useState(null);
  const [loading, setLoading]       = useState(false);
  const fetchIdRef = useRef(0);          // request counter — ignore stale responses
  const psTimerRef = useRef(null);
  const calContainerRef = useRef(null);

  const urlParams = new URLSearchParams(window.location.search);
  const isPopupMode = urlParams.get('mode') === 'create';
  
  // Preselect slot start & end times for popup mode
  const [preselectedSlot, setPreselectedSlot] = useState(null);
  const location = useLocation();

  const { defaultDate, views } = useMemo(() => ({
    defaultDate: new Date(),
    views: ['day', 'week', 'month'],
  }), []);

  const CalendarComponent = useMemo(() => (isiOS12() ? Calendar : DragAndDropCalendar), []);

  // Helper: check if a staff member is inactive (from allStaff or _inactive flag)
  const isStaffInactive = useCallback((username) => {
    const user = allStaff.find(u => u.username === username);
    if (!user) return false;
    return user._inactive || user.status === 'inactive' || user.status === 'terminated';
  }, [allStaff]);

  // ── Attach touch-to-mouse polyfill for mobile DnD ──
  useEffect(() => {
    if (isiOS12()) return;
    return addTouchDragSupport(calContainerRef.current);
  }, [calDisplayView]);

 useEffect(() => {
  if (isPopupMode) {
    //alert('Switch to "Day" View to create appointment');
    //patientCard = urlParams.get('patientCard');
    setCurrentView('day'); // open calendar in day view

    const now = new Date();
    const start = new Date(now);
    //start.setMinutes(0, 0, 0); // optional: round to hour start
const minutes = start.getMinutes();

if (minutes <= 30) {
  start.setMinutes(30, 0, 0);
} else {
  start.setHours(start.getHours() + 1);
  start.setMinutes(0, 0, 0);
}
    const end = new Date(start);
    end.setHours(end.getHours() + 1); // 1 hour later

    setPreselectedSlot({ start, end });
  }
}, []);

useEffect(() => {
  if (currentView === 'day' || currentView === 'week') {
    const scrollContainer = document.querySelector('.rbc-current-time-indicator');

    if (scrollContainer) {
      const now = new Date();
      const hour = now.getHours();
      const scrollPosition = hour * 100; // approx height per hour

      scrollContainer.scrollTop = scrollPosition;
    }
  }
}, [currentView]);

useEffect(() => {
  if (preselectedSlot && currentView === 'day') {
     const resourceId = resources.includes(auth.username) ? auth.username : "veena";
    handleSelectSlot({
      start: preselectedSlot.start,
      end: preselectedSlot.end,
      resourceId: resourceId,
      username: resourceId,
    });
  }
}, [preselectedSlot, currentView]);



  // ── Centralized date-range helper ──────────────────────────────────────────
  const getDateRange = (view, date) => {
    const d = new Date(date || new Date());
    const localStr = dt =>
      `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
    if (view === 'day')  { const s = localStr(d); return { from: s, to: s }; }
    if (view === 'week') {
      const from = new Date(d); const day = from.getDay();
      from.setDate(from.getDate() - day + (day === 0 ? -6 : 1));
      const to = new Date(from); to.setDate(from.getDate() + 6);
      return { from: localStr(from), to: localStr(to) };
    }
    const from = new Date(d.getFullYear(), d.getMonth(), 1);
    const to   = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { from: localStr(from), to: localStr(to) };
  };

  // ── Core data fetch — ALL params explicit, uses fetchIdRef to ignore stale ──
  const fetchCalData = useCallback((resList, view, date, branch, staffList) => {
    const id = ++fetchIdRef.current;
    setLoading(true);

    const { from, to } = getDateRange(view, date);
    const qs = `?from=${from}&to=${to}`;
    const isSelfOnly = resList.length === 1 && resList[0].username === auth.username;
    const branchQs = (branch && branch !== '__ALL__') ? `&branch=${encodeURIComponent(branch)}` : '';
    const url = isSelfOnly
      ? `/appointments/therapist/${auth.username}${qs}${branchQs}`
      : `/appointments/${qs}${branchQs}`;

    console.log('[Cal] fetchCalData →', { url, branch, view, from, to, users: resList.length });

    // Appointments
    const apptP = api.get(process.env.REACT_APP_URL + url).then(res => {
      console.log(`[Cal] Appointments received: ${(res.data||[]).length}, branch values:`,
        [...new Set((res.data||[]).map(a => a.branch).filter(Boolean))]);
      return res.data || [];
    });

    // Checklist tasks — single batch call instead of N parallel calls
    const usernames = resList.map(u => u.username).filter(Boolean);
    const taskP = usernames.length > 0
      ? api.get(`${process.env.REACT_APP_URL}/checklists/?assignees=${usernames.join(',')}`)
          .then(res => res.data || [])
          .catch(() => [])
      : Promise.resolve([]);

    Promise.all([apptP, taskP]).then(([data, tasks]) => {
      if (fetchIdRef.current !== id) return; // stale — a newer fetch is in progress
      setLoading(false);

      // Set appointments
      setEvents(data.map(ev => ({ ...ev, username: ev.username })));

      // Set checklist tasks in range — expand recurrences into individual calendar events
      const fromDate = new Date(from + 'T00:00:00');
      const toDate   = new Date(to   + 'T23:59:59');
      const taskEvents = [];
      tasks.forEach(t => {
        const recs = Array.isArray(t.recurrences) ? t.recurrences : [];
        // Find recurrences that fall within the visible date range
        const matchingRecs = recs.filter(r => {
          const rd = new Date(r.date);
          return rd >= fromDate && rd <= toDate;
        });
        // Create one calendar event per matching recurrence date
        matchingRecs.forEach(r => {
          const rd = new Date(r.date);
          // Check if this recurrence is already completed or N/A
          const isCompleted = (t.completions || []).some(c => {
            const cd = new Date(c.date);
            return cd.getFullYear() === rd.getFullYear() && cd.getMonth() === rd.getMonth() && cd.getDate() === rd.getDate();
          });
          taskEvents.push({
            _id: t._id, title: t.title || 'Task',
            username: t.assignee, resourceId: t.assignee,
            start: rd,
            end: new Date(rd.getTime() + 3600000),
            type: 'Task', status: isCompleted ? 'completed' : (t.status || 'pending'),
            urgentLevel: t.urgentLevel, isChecklist: true,
            recurrenceLabel: r.label,
          });
        });
        // Also show tasks with no recurrences but a startDate in range
        if (recs.length === 0 && t.startDate) {
          const sd = new Date(t.startDate);
          if (sd >= fromDate && sd <= toDate) {
            taskEvents.push({
              _id: t._id, title: t.title || 'Task',
              username: t.assignee, resourceId: t.assignee,
              start: sd,
              end: t.endDate ? new Date(t.endDate) : new Date(sd.getTime() + 3600000),
              type: 'Task', status: t.status, urgentLevel: t.urgentLevel, isChecklist: true,
            });
          }
        }
      });
      console.log(`[Cal] Checklists: ${tasks.length} fetched, ${taskEvents.length} events in range ${from}→${to}`);
      setChecklistEvents(taskEvents);

      // Auto-include staff who have appointments but aren't in resources —
      // ONLY when all staff are selected (no explicit user filter).
      // When specific users are selected, respect the selection.
      const allActive = (staffList || []).filter(u => u.status !== 'inactive' && u.status !== 'terminated');
      const isAllSelected = !isSelfOnly && resList.length >= allActive.length;
      if (isAllSelected && data.length > 0) {
        const currentRes = new Set(resList.map(u => (u.username || '').toLowerCase()));
        const evUsers    = new Set(data.map(ev => ev.username).filter(Boolean));
        const missing    = [...evUsers].filter(u => !currentRes.has((u || '').toLowerCase()));
        if (missing.length > 0) {
          const sList = staffList || [];
          const extras = missing.map(uname => {
            const found = sList.find(s => (s.username || '').toLowerCase() === (uname || '').toLowerCase());
            const isInact = found ? (found.status === 'inactive' || found.status === 'terminated') : true;
            return found ? { ...found, _inactive: isInact } : { username: uname, roles: [], _inactive: true };
          });
          setResources(prev => {
            const existing = new Set(prev.map(r => (r.username || '').toLowerCase()));
            const toAdd = extras.filter(e => !existing.has((e.username || '').toLowerCase()));
            return toAdd.length === 0 ? prev : [...prev, ...toAdd].sort((a, b) => (a.username || '').localeCompare(b.username || ''));
          });
        }
      }
    }).catch(err => {
      if (fetchIdRef.current === id) setLoading(false);
      console.error('[Cal] fetchCalData error:', err);
    });
  }, [auth.username]);

  // ── Initial load ──────────────────────────────────────────────────────────
  const initDoneRef = useRef(false);  // gates ALL change-effects until initial load completes
  useEffect(() => {
    const canSeeAll  = canDo(auth, 'appointments.view');

    // 1. Fetch branches first to determine correct default
    const branchP = api.get(process.env.REACT_APP_URL + "/branches")
      .then(res => res.data || []).catch(() => []);

    // 2. Fetch staff (including inactive)
    const staffP = canSeeAll
      ? api.get(`${process.env.REACT_APP_URL}/users/all?includeInactive=true`)
          .then(res => (res.data || []).filter(u => u.username))
          .catch(() => [{ username: auth.username, roles: auth.roles || [] }])
      : Promise.resolve([{ username: auth.username, roles: auth.roles || [] }]);

    Promise.all([branchP, staffP]).then(([branchList, staff]) => {
      // ── Resolve branch ──
      setBranches(branchList);
      const activeBranches = branchList.filter(b => b.isActive !== false && b.status !== 'inactive');
      // Priority: user's assigned branch (case-insensitive) → MedicalYogaTherapyCentre (or similar) → first active → first any
      const ciMatch = (name) => activeBranches.find(b => b.name.toLowerCase() === (name || '').toLowerCase());
      const match = ciMatch(auth.branch)
                 || ciMatch('MedicalYogaTherapyCentre')
                 || activeBranches.find(b => /medical\s*yog/i.test(b.name))
                 || activeBranches[0]
                 || branchList[0];
      const effectiveBranch = match ? match.name : (activeBranches[0]?.name || branchList[0]?.name || '');
      console.log('[Cal] Branch resolution:', { authBranch: auth.branch, activeBranches: activeBranches.map(b=>b.name), effectiveBranch });
      setNewBranch(effectiveBranch);

      // ── Resolve staff ──
      if (staff.length === 0) staff = [{ username: auth.username, roles: auth.roles || [] }];
      setAllStaff(staff);
      const initSel = new Set([auth.username]);
      setSelectedUsernames(initSel);
      const meRes = staff.filter(u => u.username === auth.username);
      const startRes = meRes.length > 0 ? meRes : [{ username: auth.username, roles: auth.roles || [] }];
      setResources(startRes);

      // ── Single fetch with correct branch from the start ──
      fetchCalData(startRes, currentView, currentDate, effectiveBranch, staff);

      // Mark init done on NEXT tick so the state-change effects from above
      // (setNewBranch, setSelectedUsernames) fire first and see initDone=false → skip
      setTimeout(() => { initDoneRef.current = true; }, 0);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Branch change → re-fetch ──────────────────────────────────────────────
  const prevBranchRef = useRef(null);
  useEffect(() => {
    if (!initDoneRef.current) return; // wait for initial load
    if (prevBranchRef.current === newBranch) return;
    prevBranchRef.current = newBranch;
    if (resources.length === 0) return;
    fetchCalData(resources, currentView, currentDate, newBranch, allStaff);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newBranch]);

  // ── User selection change → rebuild resources + re-fetch ──────────────────
  useEffect(() => {
    if (!initDoneRef.current) return; // wait for initial load
    if (!selectedUsernames || allStaff.length === 0) return;
    const sel = allStaff.filter(u => selectedUsernames.has(u.username));
    if (sel.length === 0) return;
    setResources(sel);
    if (selectedUsernames.size === 1 && selectedUsernames.has(auth.username)) {
      setLeaveForWhom('Self');
    } else {
      setLeaveForWhom('All');
    }
    fetchCalData(sel, currentView, currentDate, newBranch, allStaff);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUsernames]);

  // ── Date / view navigation → re-fetch ─────────────────────────────────────
  const prevDateViewRef = useRef(null);
  useEffect(() => {
    if (!initDoneRef.current) return; // wait for initial load
    const key = `${currentDate}|${currentView}`;
    if (prevDateViewRef.current === key) return;
    prevDateViewRef.current = key;
    if (resources.length === 0) return;
    fetchCalData(resources, currentView, currentDate, newBranch, allStaff);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, currentView]);

  useEffect(() => { setMyEvents(events); }, [events]);

  const formatDateTime = dateStr => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const formattedEvents = useMemo(() => {
    if (!Array.isArray(myEvents) || !Array.isArray(resources)) return [];
    const validUsernames = new Set(resources.map(r => r.username));

    const apptEvents = myEvents
      .filter(event => {
        if (!event.start || !event.end) return false;
        const start = new Date(event.start);
        const end = new Date(event.end);
        if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;
        // Strictly filter to selected users — do NOT reassign username
        if (!validUsernames.has(event.username)) return false;
        return true;
      })
      .map(event => ({
        ...event,
        start: new Date(event.start),
        end: new Date(event.end),
        resourceId: (event.leadTherapist && (event.status === 'inprogress' || event.status === 'Completed'))
          ? event.leadTherapist
          : event.username,
        title: event.title || "Untitled",
        formattedStart: formatDateTime(event.start),
        formattedEnd: formatDateTime(event.end),
        type: event.type || 'Therapy'
      }));

    // Merge checklist tasks only when toggle is on
    if (!showTasks) return apptEvents;

    const taskEvents = checklistEvents
      .filter(e => validUsernames.has(e.username))
      .map(e => ({
        ...e,
        formattedStart: formatDateTime(e.start),
        formattedEnd:   formatDateTime(e.end),
      }));

    return [...apptEvents, ...taskEvents];
  }, [myEvents, resources, checklistEvents, showTasks]);

  // ── Create appointment for a given patient card ──
  const createApptForPatient = useCallback((cardId, slot) => {
    const { start, end, resourceId } = slot;
    const popupMode = new URLSearchParams(window.location.search).get('mode') === 'create';
    api.get(`${process.env.REACT_APP_URL}/patients/profiles/${cardId}`)
      .then(res => {
        const name = res.data.name;
        const isOnline = calOnlineMode;
        const newEv = {
          start, end,
          username: resourceId,
          formattedStart: formatDateTime(start),
          formattedEnd: formatDateTime(end),
          name,
          patientCard: cardId,
          date: new Date(),
          title: `${name} / ${res.data.number}`,
          type: 'Therapy',
          branch: newBranch === '__ALL__' ? (auth.branch || 'MedicalYogaTherapyCentre') : newBranch,
          status: "scheduled",
          isOnline: !!isOnline,
        };
        setMyEvents(prev => [...prev, newEv]);
        api.post(`${process.env.REACT_APP_URL}/appointments/dynamic/add/`, newEv)
          .then(() => {
            showAlert("Appointment Scheduled ✅", 'success');
            fetchCalData(resources, currentView, currentDate, newBranch, allStaff);
            if (isOnline && res.data.number) {
              const phone = String(res.data.number).replace(/\D/g, '');
              const meetLink = localStorage.getItem('online_meet_url') || '';
              const zoomLink = localStorage.getItem('online_zoom_url') || '';
              const link = meetLink || zoomLink || '';
              if (phone && link) {
                const dtStr = new Date(start).toLocaleString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
                sendWhatsApp(phone, `Hi ${name}, your online session is scheduled for ${dtStr}. Join here: ${link}`);
              }
            }
            if (popupMode) {
              window.opener?.location.reload();
              window.close();
            }
          })
          .catch((err) => showAlert(`❌ Error scheduling appointment. ${err}`, 'error'));
      })
      .catch(() => showAlert("Patient not found with this ID", 'error'));
  }, [setMyEvents, showAlert, calOnlineMode, sendWhatsApp, newBranch, fetchCalData, resources, currentView, currentDate, allStaff]);

  // ── Patient search handlers ──
  function psSearch(q) {
    setPsQuery(q);
    if (psTimerRef.current) clearTimeout(psTimerRef.current);
    if (q.length < 4) { setPsResults([]); return; }
    setPsLoading(true);
    psTimerRef.current = setTimeout(() => {
      api.get(`${process.env.REACT_APP_URL}/patients/profiles/search/${encodeURIComponent(q)}`)
        .then(r => setPsResults(r.data || []))
        .catch(() => setPsResults([]))
        .finally(() => setPsLoading(false));
    }, 400);
  }

  function psSelectPatient(patient) {
    setPsOpen(false);
    setPsQuery(''); setPsResults([]); setPsNewMode(false);
    createApptForPatient(patient.patientCard, pendingSlot);
  }

  async function psCreateAndBook() {
    const { name, number, age, gender, purpose } = psNewForm;
    if (!name.trim() || !number.trim()) { showAlert('Name and Mobile are required', 'error'); return; }
    setPsCreating(true);
    try {
      const res = await api.post(`${process.env.REACT_APP_URL}/patients/profiles/add/auto`, {
        name: name.trim(), number: number.trim(), age, gender, purpose,
        branch: (newBranch && newBranch !== '__ALL__') ? newBranch : (auth.branch || 'MedicalYogaTherapyCentre'), status: 'provisional',
      });
      const newCard = res.data.patientCard;
      setPsOpen(false);
      setPsQuery(''); setPsResults([]); setPsNewMode(false);
      setPsNewForm({ name: '', number: '', age: '', gender: 'male', purpose: '' });
      createApptForPatient(newCard, pendingSlot);
    } catch (e) {
      showAlert('Error creating patient: ' + (e.response?.data || e.message), 'error');
    } finally {
      setPsCreating(false);
    }
  }

  const handleSelectSlot = useCallback(async ({ start, end, resourceId }) => {
    const isPopupMode = new URLSearchParams(window.location.search).get('mode') === 'create';
    var patientCard = new URLSearchParams(window.location.search).get('patientCard');
    var therapistName = new URLSearchParams(window.location.search).get('therapistName');

    if (!patientCard) {
      // Open search modal instead of prompt
      setPendingSlot({ start, end, resourceId });
      setPsOpen(true);
      return;
    }

    // URL-param mode (popup from patient dashboard)
    resourceId = therapistName;
    if (!isPopupMode)
      showAlert(`New Appointment for ${patientCard} with ${resourceId}`, 'info');
    createApptForPatient(patientCard, { start, end, resourceId });
  }, [showAlert, createApptForPatient]);

  const handleEventClick = useCallback(event => {
    setDetailEvent(event);
  }, []);

  const handleDetailSave = useCallback((updatedFields) => {
    api.post(`${process.env.REACT_APP_URL}/appointments/update/${detailEvent._id}`, updatedFields)
      .then(() => {
        showAlert('Appointment updated ✅', 'success');
        fetchCalData(resources, currentView, currentDate, newBranch, allStaff);
        setDetailEvent(null);
      })
      .catch(() => showAlert('Update failed', 'error'));
  }, [detailEvent, resources, showAlert, fetchCalData, currentView, currentDate, newBranch, allStaff]);

  const handleDetailDelete = useCallback((reason) => {
    api.delete(`${process.env.REACT_APP_URL}/appointments/${detailEvent._id}`, { data: { reason } })
      .then(() => {
        showAlert('Appointment deleted', 'success');
        setMyEvents(prev => prev.filter(e => e._id !== detailEvent._id));
        setDetailEvent(null);
      })
      .catch(() => showAlert('Delete failed', 'error'));
  }, [detailEvent, showAlert, setMyEvents]);

  const handleChange = (field, value) => {
    if (field === 'leaveforwhom') setLeaveForWhom(value);
    if (field === 'branch') setNewBranch(value);
  };

  const moveEvent = useCallback(
    ({ event, start, end, resourceId, isAllDay: droppedOnAllDaySlot = false }) => {
      const { allDay, username } = event;

      // Prevent moving a completed Therapy event
      const now = new Date();                 
      //if (new Date(event.start) < now) {
      if (event.type === 'Therapy' && event.status === "Completed" && event.status === 'Skipped') {
        alert("Event is in progress/finished. Can't change now");
        return;
      }

      console.log('moveEvent = ', event, start, end, resourceId, username);

      // If the event is not all-day and is dropped onto an all-day slot, mark it as all-day
      if (!allDay && droppedOnAllDaySlot) {
        event.allDay = true;
      }

      // If resourceId is different from the current username, it's a handover case
      if (resourceId !== username) {
        event.handoverFrom = username; // Record who is handing over the event
        event.handoverTo = resourceId; // Record who is receiving the event
        event.username = resourceId; // Update the event's resource/username
        event.resourceId = resourceId; // Update the event's resourceId
      }

      // Update the event's start and end times
      event.start = start;
      event.end = end;

      // Update the event in the state (replace the existing event with the updated one)
      setMyEvents((prev) => {
        const existing = prev.find((ev) => ev._id === event._id) ?? {};
        const filtered = prev.filter((ev) => ev._id !== event._id);

        return [
          ...filtered,
          {
            ...existing,
            start,
            end,
            resourceId,
            allDay,
            username: resourceId,
            handoverFrom: username,
            handoverTo: resourceId,
          },
        ];
      });

      // Make the API call to update the event on the server
      api
        .post(`${process.env.REACT_APP_URL}/appointments/update/${event._id}`, event)
        .then((response) => {
          console.log(response.data);
        })
        .catch((error) => console.error('Error updating event:', error));
    },
    [setMyEvents]
  );

  const handleEventDrop = useCallback(
    ({ event, start, end, resourceId, isAllDay: droppedOnAllDaySlot = false }) => {
      // Call the moveEvent callback to handle the event update logic
      moveEvent({
        event,
        start,
        end,
        resourceId,
        isAllDay: droppedOnAllDaySlot,
      });
    },
    [moveEvent]
  );

  const eventPropGetter = event => {
      let bg = '#3b82f6'; let fg = '#fff'; let border = 'none';
      if (event.type === 'holiday' || event.type === 'Leave') { bg = '#fee2e2'; fg = '#b91c1c'; border = '1px solid #fca5a5'; }
      else if (event.type === 'FlowTask')   { bg = '#e0e7ff'; fg = '#3730a3'; border = '1px solid #a5b4fc'; }
      else if (event.type === 'Task')       { bg = '#fef9c3'; fg = '#854d0e'; border = '1px solid #fde047'; }
      else if (event.bookedByPatient && event.status === 'pending') { bg = '#fef3c7'; fg = '#92400e'; border = '1px solid #fbbf24'; }
      else if (event.status === 'Completed')  { bg = '#dcfce7'; fg = '#166534'; border = '1px solid #86efac'; }
      else if (event.status === 'Skipped')    { bg = '#f5f5f4'; fg = '#78716c'; border = '1px solid #d6d3d1'; }
      else if (event.status === 'scheduled')  { bg = '#dbeafe'; fg = '#1e40af'; border = '1px solid #93c5fd'; }
      else if (event.status === 'inprogress') { bg = '#d1fae5'; fg = '#065f46'; border = '1px solid #6ee7b7'; }
      else { bg = '#f1f5f9'; fg = '#334155'; border = '1px solid #cbd5e1'; }
      // Dim events from inactive staff
      const inactive = isStaffInactive(event.username);
      // Dashed border for handed-over appointments
      if (event.handoverGiven) border = '2px dashed #6366f1';
      return { style: {
        background: bg, color: fg, border,
        borderRadius: 8, fontSize: 13, fontWeight: 600,
        padding: '2px 6px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis',
        opacity: inactive ? 0.55 : 1,
      }};
  };

  // ── Picker button mini-style helper ──────────────────────────────────────────
  const pickerBtnStyle = (color) => ({
    fontSize: 11, padding: '3px 10px', borderRadius: 5, border: `1px solid ${color}`,
    background: '#fff', color, fontWeight: 700, cursor: 'pointer',
  });

  // Appointment status counts for toolbar
  const escalatedCount  = formattedEvents.filter(e => e.escalated).length;
  const inprogressCount = formattedEvents.filter(e => e.status === 'inprogress').length;
  const scheduledCount  = formattedEvents.filter(e => e.status === 'scheduled').length;
  const completedCount  = formattedEvents.filter(e => e.status === 'Completed').length;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#f8fafc' }}
      onClick={e => {
        // Close picker if click is outside the picker element
        if (showUserPicker && !e.target.closest('[data-userpicker]')) setShowUserPicker(false);
      }}>
      {dialogNode}

      {/* ── Loading overlay ── */}
      {loading && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 8500, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(248,250,252,0.6)', pointerEvents: 'none' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 36, height: 36, border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>Loading...</span>
          </div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* ── Patient Search Modal ── */}
      {psOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={e => { if (e.target === e.currentTarget) { setPsOpen(false); setPsNewMode(false); setPsQuery(''); setPsResults([]); } }}>
          <div style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: 480, maxHeight: '85vh', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg,#1e3a5f,#2563eb)', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ color: '#fff', fontWeight: 800, fontSize: 15 }}>Search Patient</div>
              <button onClick={() => { setPsOpen(false); setPsNewMode(false); setPsQuery(''); setPsResults([]); }}
                style={{ background: 'rgba(255,255,255,0.18)', border: 'none', color: '#fff', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>✕</button>
            </div>

            <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
              {/* Search Input */}
              <input
                autoFocus placeholder="Type name, card ID, or phone (min 4 chars)..."
                value={psQuery} onChange={e => psSearch(e.target.value)}
                style={{ width: '100%', padding: '12px 16px', border: '1.5px solid #e2e8f0', borderRadius: 12, fontSize: 14, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }}
              />

              {/* Results */}
              {psLoading && <div style={{ textAlign: 'center', padding: 16, color: '#64748b', fontSize: 13 }}>Searching...</div>}
              {!psLoading && psQuery.length >= 4 && psResults.length === 0 && (
                <div style={{ textAlign: 'center', padding: 16, color: '#94a3b8', fontSize: 13 }}>No patients found</div>
              )}
              {psResults.length > 0 && (
                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {psResults.slice(0, 8).map(p => (
                    <div key={p._id || p.patientCard} onClick={() => psSelectPatient(p)}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                      <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,#2563eb,#0891b2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 14, flexShrink: 0 }}>
                        {(p.name || '?')[0].toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>
                          <span style={{ fontFamily: 'monospace', background: '#f1f5f9', borderRadius: 4, padding: '1px 5px', marginRight: 6 }}>{p.patientCard}</span>
                          {p.number || ''}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add New Patient toggle */}
              {!psNewMode && (
                <div style={{ marginTop: 16, textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>Patient not found?</div>
                  <button onClick={() => setPsNewMode(true)}
                    style={{ background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 10, padding: '10px 22px', fontSize: 13, fontWeight: 700, color: '#166534', cursor: 'pointer' }}>
                    + Add New Patient
                  </button>
                </div>
              )}

              {/* New Patient Form */}
              {psNewMode && (
                <div style={{ marginTop: 16, background: '#f8fafc', borderRadius: 14, padding: 16, border: '1px solid #e2e8f0' }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#1e3a5f', marginBottom: 12 }}>New Patient (Provisional)</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <input placeholder="Full Name *" value={psNewForm.name}
                      onChange={e => setPsNewForm(f => ({ ...f, name: e.target.value }))}
                      style={{ padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' }} />
                    <input placeholder="Mobile Number *" type="tel" value={psNewForm.number}
                      onChange={e => setPsNewForm(f => ({ ...f, number: e.target.value }))}
                      style={{ padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' }} />
                    <div style={{ display: 'flex', gap: 10 }}>
                      <input placeholder="Age" type="number" value={psNewForm.age}
                        onChange={e => setPsNewForm(f => ({ ...f, age: e.target.value }))}
                        style={{ padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, flex: 1, fontFamily: 'inherit' }} />
                      <select value={psNewForm.gender} onChange={e => setPsNewForm(f => ({ ...f, gender: e.target.value }))}
                        style={{ padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, flex: 1, fontFamily: 'inherit' }}>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <input placeholder="Purpose of Visit" value={psNewForm.purpose}
                      onChange={e => setPsNewForm(f => ({ ...f, purpose: e.target.value }))}
                      style={{ padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' }} />
                    <button onClick={psCreateAndBook} disabled={psCreating}
                      style={{ padding: '12px 0', background: psCreating ? '#94a3b8' : 'linear-gradient(135deg,#059669,#10b981)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: psCreating ? 'not-allowed' : 'pointer' }}>
                      {psCreating ? 'Creating...' : 'Create & Book Appointment'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── TOOLBAR ── */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', padding: '10px 18px',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0,
      }}>

        {/* Left: user picker + branch + mode */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>

          {/* ── People checkbox picker ── */}
          <div style={{ position: 'relative' }} data-userpicker>
            <button
              onClick={e => { e.stopPropagation(); setShowUserPicker(v => !v); }}
              style={{
                padding: '6px 14px', borderRadius: 8,
                border: `1.5px solid ${showUserPicker ? '#60a5fa' : 'rgba(255,255,255,0.15)'}`,
                background: showUserPicker ? 'rgba(96,165,250,0.15)' : 'rgba(255,255,255,0.06)',
                color: showUserPicker ? '#93c5fd' : '#e2e8f0',
                fontWeight: 600, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                backdropFilter: 'blur(8px)', transition: 'all 0.2s',
              }}>
              👥 {selectedUsernames ? selectedUsernames.size : 1} selected ▾
            </button>

            {showUserPicker && selectedUsernames && (
              <div style={{
                position: 'absolute', top: '110%', left: 0, zIndex: 1000,
                background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12,
                boxShadow: '0 12px 40px rgba(0,0,0,.5)', padding: 14, minWidth: 250, maxHeight: 380, overflowY: 'auto',
              }}
                onMouseLeave={() => {}}>

                {/* Header actions */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <button onClick={() => setSelectedUsernames(new Set(allStaff.filter(u => u.status !== 'inactive' && u.status !== 'terminated').map(u => u.username)))}
                    style={{ ...pickerBtnStyle('#2563eb'), background: 'rgba(37,99,235,0.2)', color: '#93c5fd', border: '1px solid rgba(37,99,235,0.3)' }}>All Active</button>
                  <button onClick={() => setSelectedUsernames(new Set([auth.username]))}
                    style={{ ...pickerBtnStyle('#059669'), background: 'rgba(5,150,105,0.2)', color: '#6ee7b7', border: '1px solid rgba(5,150,105,0.3)' }}>Only Me</button>
                  <button onClick={() => setShowUserPicker(false)}
                    style={{ ...pickerBtnStyle('#6b7280'), background: 'rgba(107,114,128,0.2)', color: '#d1d5db', border: '1px solid rgba(107,114,128,0.3)' }}>Done</button>
                </div>

                {[...allStaff]
                  .sort((a, b) => {
                    // Active staff first, then inactive
                    const aInact = a.status === 'inactive' || a.status === 'terminated';
                    const bInact = b.status === 'inactive' || b.status === 'terminated';
                    if (aInact !== bInact) return aInact ? 1 : -1;
                    return (a.username || '').localeCompare(b.username || '');
                  })
                  .map(u => {
                  const isMe    = u.username === auth.username;
                  const checked = selectedUsernames.has(u.username);
                  const inactive = u.status === 'inactive' || u.status === 'terminated';
                  return (
                    <label key={u.username} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 4px', cursor: 'pointer', borderRadius: 6, transition: 'background 0.15s', opacity: inactive ? 0.5 : 1 }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <input type="checkbox" checked={checked}
                        onChange={() => {
                          setSelectedUsernames(prev => {
                            const next = new Set(prev);
                            next.has(u.username) ? (next.size > 1 && next.delete(u.username)) : next.add(u.username);
                            return next;
                          });
                        }}
                        style={{ width: 15, height: 15, accentColor: inactive ? '#6b7280' : '#60a5fa', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: 13, fontWeight: isMe ? 700 : 400, color: isMe ? '#93c5fd' : inactive ? '#64748b' : '#cbd5e1' }}>
                        {u.username}{isMe ? ' (me)' : ''}{inactive ? ' (inactive)' : ''}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <select id="branch" value={newBranch} onChange={e => handleChange('branch', e.target.value)}
            style={{
              padding: '6px 12px', fontSize: 12, fontWeight: 600,
              border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8,
              background: 'rgba(255,255,255,0.06)', color: '#e2e8f0',
              cursor: 'pointer', appearance: 'auto',
            }}>
            <option value="__ALL__" style={{ background: '#1e293b', color: '#e2e8f0' }}>All Branches</option>
            {branches.filter(b => b.isActive !== false && b.status !== 'inactive').map(b => <option key={b._id || b.name} value={b.name} style={{ background: '#1e293b', color: '#e2e8f0' }}>{b.name}</option>)}
          </select>

          <button onClick={() => setCalOnlineMode(v => !v)}
            style={{
              padding: '6px 14px', borderRadius: 8, fontWeight: 600, fontSize: 12, cursor: 'pointer',
              border: `1.5px solid ${calOnlineMode ? 'rgba(16,185,129,0.4)' : 'rgba(255,255,255,0.12)'}`,
              background: calOnlineMode ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.06)',
              color: calOnlineMode ? '#6ee7b7' : '#94a3b8',
              transition: 'all 0.2s',
            }}>
            {calOnlineMode ? '🌐 Online' : '📍 Centre'}
          </button>

          <button onClick={() => setShowTasks(v => !v)}
            style={{
              padding: '6px 14px', borderRadius: 8, fontWeight: 600, fontSize: 12, cursor: 'pointer',
              border: `1.5px solid ${showTasks ? 'rgba(251,191,36,0.4)' : 'rgba(255,255,255,0.12)'}`,
              background: showTasks ? 'rgba(251,191,36,0.15)' : 'rgba(255,255,255,0.06)',
              color: showTasks ? '#fcd34d' : '#94a3b8',
              transition: 'all 0.2s',
            }}>
            {showTasks ? `Tasks (${checklistEvents.length})` : 'Tasks'}
          </button>
        </div>

        {/* Centre: status chips */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginLeft: 6 }}>
          {scheduledCount  > 0 && <span style={{ background: 'rgba(59,130,246,0.15)', color: '#93c5fd', borderRadius: 20, padding: '3px 12px', fontSize: 11, fontWeight: 600, border: '1px solid rgba(59,130,246,0.25)' }}>{scheduledCount} Sched</span>}
          {inprogressCount > 0 && <span style={{ background: 'rgba(16,185,129,0.15)', color: '#6ee7b7', borderRadius: 20, padding: '3px 12px', fontSize: 11, fontWeight: 600, border: '1px solid rgba(16,185,129,0.25)' }}>{inprogressCount} Active</span>}
          {completedCount  > 0 && <span style={{ background: 'rgba(34,197,94,0.15)', color: '#86efac', borderRadius: 20, padding: '3px 12px', fontSize: 11, fontWeight: 600, border: '1px solid rgba(34,197,94,0.25)' }}>{completedCount} Done</span>}
          {escalatedCount  > 0 && (
            <span style={{ background: 'rgba(239,68,68,0.15)', color: '#fca5a5', borderRadius: 20, padding: '3px 12px', fontSize: 11, fontWeight: 600, border: '1px solid rgba(239,68,68,0.3)', cursor: 'pointer' }}
              onClick={() => setCalDisplayView('list')}>
              {escalatedCount} Escalated
            </span>
          )}
        </div>

        {/* Right: view toggle */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}>
          <button onClick={() => setCalDisplayView('calendar')}
            style={{
              padding: '7px 18px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
              background: calDisplayView === 'calendar' ? 'rgba(59,130,246,0.25)' : 'rgba(255,255,255,0.04)',
              color: calDisplayView === 'calendar' ? '#93c5fd' : '#64748b',
              transition: 'all 0.2s',
            }}>
            Calendar
          </button>
          <button onClick={() => setCalDisplayView('list')}
            style={{
              padding: '7px 18px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
              background: calDisplayView === 'list' ? 'rgba(59,130,246,0.25)' : 'rgba(255,255,255,0.04)',
              color: calDisplayView === 'list' ? '#93c5fd' : '#64748b',
              transition: 'all 0.2s',
            }}>
            List
          </button>
        </div>
      </div>

      {/* ── CALENDAR VIEW ─────────────────────────────────── */}
      {calDisplayView === 'calendar' && (
        <div ref={calContainerRef} style={{ flex: 1, overflowX: 'auto', overflowY: 'auto', background: '#f8fafc' }}>
          <CalendarComponent
            selectable
            localizer={localizer}
            events={formattedEvents}
            view={currentView}
            onView={v => setCurrentView(v)}
            date={currentDate}
            onNavigate={d => setCurrentDate(d)}
            views={views}
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleEventClick}
            onEventDrop={!isiOS12() ? handleEventDrop : undefined}
            onEventResize={!isiOS12() ? ({ event, start, end }) => {
              const idx = myEvents.findIndex(e => e._id === event._id || e === event);
              const updated = [...myEvents];
              updated[idx] = { ...event, start, end };
              setMyEvents(updated);
              api.post(`${process.env.REACT_APP_URL}/appointments/update/${event._id}`, { start, end });
            } : undefined}
            resizable={!isiOS12()}
            resources={resources}
            resourceIdAccessor="username"
            resourceTitleAccessor={r => r._inactive || isStaffInactive(r.username) ? `${r.username} (inactive)` : r.username}
            eventPropGetter={eventPropGetter}
            style={{
              height: 'calc(100vh - 62px)',
              minWidth: resources.length > 1 ? resources.length * 220 + 70 : undefined,
              padding: '0 2px',
            }}
          />
        </div>
      )}

      {/* ── LIST VIEW ─────────────────────────────────────── */}
      {calDisplayView === 'list' && (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <ListViewPanel
            events={formattedEvents}
            onEdit={setDetailEvent}
            onEscalate={setEscalationEvent}
            isStaffInactive={isStaffInactive}
          />
        </div>
      )}

      {/* ── APPOINTMENT DETAIL / EDIT MODAL ───────────────── */}
      {detailEvent && (
        <AppointmentDetailModal
          event={detailEvent}
          onClose={() => setDetailEvent(null)}
          onSave={handleDetailSave}
          onDelete={handleDetailDelete}
          onEscalate={(ev) => { setDetailEvent(null); setEscalationEvent(ev); }}
        />
      )}

      {/* ── ESCALATION MODAL ──────────────────────────────── */}
      {escalationEvent && (
        <EscalationModal
          appointment={escalationEvent}
          patientCard={escalationEvent.patientCard}
          onClose={() => setEscalationEvent(null)}
          refreshSequences={() => fetchCalData(resources, currentView, currentDate, newBranch, allStaff)}
          variant="modal"
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   APPOINTMENT DETAIL / EDIT MODAL
═══════════════════════════════════════════════════════════ */
function toLocalDT(dt) {
  if (!dt) return '';
  const d = new Date(dt), p = n => String(n).padStart(2,'0');
  return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/* ═══════════════════════════════════════════════════════════
   LIST VIEW PANEL — search, filter, sortable table
═══════════════════════════════════════════════════════════ */
function ListViewPanel({ events, onEdit, onEscalate, isStaffInactive }) {
  const [search,           setSearch]        = useState('');
  const [filterStatus,     setFilterStatus]  = useState('all');
  const [filterType,       setFilterType]    = useState('all');
  const [filterMode,       setFilterMode]    = useState('all');
  const [filterFrom,       setFilterFrom]    = useState('');
  const [filterTo,         setFilterTo]      = useState('');
  const [sortDir,          setSortDir]       = useState('desc');
  const [filterEscalated,  setFilterEscalated] = useState(false);
  const [filterHandover,   setFilterHandover]  = useState(false);

  const statuses = ['all', 'scheduled', 'inprogress', 'Completed', 'pending', 'cancelled'];
  const types    = ['all', ...Array.from(new Set((events || []).map(e => e.type).filter(Boolean)))];

  const statusColor = { Completed:'#059669', scheduled:'#2563eb', inprogress:'#d97706', pending:'#92400e', cancelled:'#dc2626' };

  const filtered = (events || []).filter(ev => {
    const q = search.toLowerCase();
    if (q && !(
      (ev.name||'').toLowerCase().includes(q) ||
      (ev.title||'').toLowerCase().includes(q) ||
      (ev.patientCard||'').toLowerCase().includes(q) ||
      (ev.username||'').toLowerCase().includes(q)
    )) return false;
    if (filterEscalated && !ev.escalated) return false;
    if (filterHandover && !ev.handoverGiven) return false;
    if (filterStatus !== 'all' && ev.status !== filterStatus) return false;
    if (filterType   !== 'all' && ev.type   !== filterType)   return false;
    if (filterMode === 'online'    && !ev.isOnline)  return false;
    if (filterMode === 'centre'    && ev.isOnline)   return false;
    if (filterMode === 'patient'   && !ev.bookedByPatient) return false;
    const evDate = ev.start ? new Date(ev.start) : null;
    if (filterFrom && evDate && evDate < new Date(filterFrom)) return false;
    if (filterTo   && evDate && evDate > new Date(filterTo + 'T23:59:59')) return false;
    return true;
  }).sort((a, b) => {
    const da = a.start ? new Date(a.start) : 0;
    const db = b.start ? new Date(b.start) : 0;
    return sortDir === 'desc' ? db - da : da - db;
  });

  const sel = { padding: '5px 8px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 6, background: '#f8fafc', width: 'auto', flexShrink: 0 };

  const escalatedInView = filtered.filter(e => e.escalated).length;

  return (
    <div style={{ padding: '12px 16px 24px' }}>
      {/* ── Filter bar ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12, alignItems: 'center', background: '#f8fafc', borderRadius: 10, padding: '10px 12px', border: '1px solid #e5e7eb' }}>
        <input
          placeholder="🔍  Search name / card / therapist…"
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: '1 1 200px', padding: '6px 10px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 6 }}
        />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={sel}>
          {statuses.map(s => <option key={s} value={s}>{s === 'all' ? 'All Statuses' : s}</option>)}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} style={sel}>
          {types.map(t => <option key={t} value={t}>{t === 'all' ? 'All Types' : t}</option>)}
        </select>
        <select value={filterMode} onChange={e => setFilterMode(e.target.value)} style={sel}>
          <option value="all">All Modes</option>
          <option value="online">🌐 Online</option>
          <option value="centre">📍 At Centre</option>
          <option value="patient">👤 Patient Booked</option>
        </select>
        <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} style={{ ...sel, paddingLeft: 6 }} title="From date" />
        <input type="date" value={filterTo}   onChange={e => setFilterTo(e.target.value)}   style={{ ...sel, paddingLeft: 6 }} title="To date" />
        <button onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}
          style={{ ...sel, cursor: 'pointer', fontWeight: 700, whiteSpace: 'nowrap' }}>
          {sortDir === 'desc' ? '↓ Newest First' : '↑ Oldest First'}
        </button>
        <button onClick={() => setFilterEscalated(v => !v)}
          style={{ ...sel, cursor: 'pointer', fontWeight: 700, whiteSpace: 'nowrap',
            background: filterEscalated ? '#fef2f2' : '#f8fafc',
            color: filterEscalated ? '#b91c1c' : '#6b7280',
            border: filterEscalated ? '1px solid #fca5a5' : '1px solid #d1d5db' }}>
          🔔 {filterEscalated ? 'Escalated Only' : 'Show Escalated'}
        </button>
        <button onClick={() => setFilterHandover(v => !v)}
          style={{ ...sel, cursor: 'pointer', fontWeight: 700, whiteSpace: 'nowrap',
            background: filterHandover ? '#eef2ff' : '#f8fafc',
            color: filterHandover ? '#4338ca' : '#6b7280',
            border: filterHandover ? '1px solid #a5b4fc' : '1px solid #d1d5db' }}>
          🔄 {filterHandover ? 'Handovers Only' : 'Show Handovers'}
        </button>
        <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
          {filtered.length} records{escalatedInView > 0 && ` · 🔔 ${escalatedInView} escalated`}
        </span>
      </div>

      {/* ── Table ── */}
      <div style={{ overflowX: 'auto', borderRadius: 12, border: '1px solid #e5e7eb', background: '#fff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#1e3a5f' }}>
              {['Date & Time','Patient','Card ID','Therapist','Type','Mode','Status','Branch',
                ...(filterHandover ? ['Handed From', 'Handed By'] : []),
                'Actions'].map(h => (
                <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#fff', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap', fontSize: 11, letterSpacing: '.3px' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((ev, i) => {
              const isEscalated     = !!ev.escalated;
              const isPatientBooked = ev.bookedByPatient;
              const bg  = isEscalated ? '#fff9f0' : i % 2 === 0 ? '#fff' : '#f9fafb';
              const sc  = statusColor[ev.status] || '#6b7280';
              return (
                <tr key={ev._id || i}
                  style={{ background: bg, cursor: 'pointer', borderLeft: isEscalated ? '3px solid #ef4444' : '3px solid transparent' }}
                  onClick={() => onEdit(ev)}>
                  <td style={{ padding: '9px 12px', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap', fontSize: 12 }}>
                    <div>{ev.start ? new Date(ev.start).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '—'}</div>
                    {isEscalated && <div style={{ fontSize: 10, color: '#ef4444', fontWeight: 700, marginTop: 2 }}>🔔 Escalated</div>}
                  </td>
                  <td style={{ padding: '9px 12px', borderBottom: '1px solid #f3f4f6', maxWidth: 140 }}>
                    <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.name || ev.title || '—'}</div>
                    {ev.patientCard && <div style={{ fontSize: 10, color: '#94a3b8' }}>{ev.patientCard}</div>}
                  </td>
                  <td style={{ padding: '9px 12px', borderBottom: '1px solid #f3f4f6', fontFamily: 'monospace', fontSize: 12 }}>{ev.patientCard || '—'}</td>
                  <td style={{ padding: '9px 12px', borderBottom: '1px solid #f3f4f6', fontSize: 12 }}>
                    {ev.username || '—'}
                    {isStaffInactive && isStaffInactive(ev.username) && (
                      <span style={{ fontSize: 9, color: '#9ca3af', fontWeight: 600, marginLeft: 4, background: '#f1f5f9', borderRadius: 4, padding: '1px 4px' }}>inactive</span>
                    )}
                  </td>
                  <td style={{ padding: '9px 12px', borderBottom: '1px solid #f3f4f6', fontSize: 12 }}>{ev.type || '—'}</td>
                  <td style={{ padding: '9px 12px', borderBottom: '1px solid #f3f4f6' }}>
                    {ev.isOnline ? <span style={{ color: '#059669', fontWeight: 700 }}>🌐</span>
                      : isPatientBooked ? <span style={{ color: '#d97706', fontWeight: 700 }}>👤</span>
                      : <span style={{ color: '#6b7280' }}>📍</span>}
                  </td>
                  <td style={{ padding: '9px 12px', borderBottom: '1px solid #f3f4f6' }}>
                    <span style={{ background: sc + '18', color: sc, borderRadius: 6, padding: '3px 9px', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap' }}>{ev.status || '—'}</span>
                  </td>
                  <td style={{ padding: '9px 12px', borderBottom: '1px solid #f3f4f6', fontSize: 12 }}>{ev.branch || '—'}</td>
                  {filterHandover && (
                    <>
                      <td style={{ padding: '9px 12px', borderBottom: '1px solid #f3f4f6', fontSize: 12 }}>
                        {ev.handoverFrom ? (
                          <span style={{ fontWeight: 600, color: '#4338ca' }}>{ev.handoverFrom}</span>
                        ) : '—'}
                      </td>
                      <td style={{ padding: '9px 12px', borderBottom: '1px solid #f3f4f6', fontSize: 12 }}>
                        {ev.handoverBy ? (
                          <span style={{ fontWeight: 600, color: ev.handoverBy === ev.handoverFrom ? '#166534' : '#7c3aed' }}>
                            {ev.handoverBy}{ev.handoverBy === ev.handoverFrom ? '' : ' (admin)'}
                          </span>
                        ) : ev.handoverFrom ? <span style={{ color: '#94a3b8' }}>—</span> : '—'}
                      </td>
                    </>
                  )}
                  <td style={{ padding: '7px 10px', borderBottom: '1px solid #f3f4f6' }} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'nowrap' }}>
                      <button onClick={() => onEdit(ev)}
                        style={{ padding: '4px 10px', background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                        ✏️ Edit
                      </button>
                      {ev.patientCard && (
                        <button onClick={() => onEscalate?.(ev)}
                          style={{ padding: '4px 10px', background: isEscalated ? '#fef2f2' : '#fff7ed', color: isEscalated ? '#b91c1c' : '#92400e', border: `1px solid ${isEscalated ? '#fca5a5' : '#fed7aa'}`, borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          🔔 {isEscalated ? 'View' : 'Escalate'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={filterHandover ? 11 : 9} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>No appointments match the current filters</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AppointmentDetailModal({ event, onClose, onSave, onDelete, onEscalate }) {
  const [editMode, setEditMode]       = useState(false);
  const [confirmDel, setConfirmDel]   = useState(false);
  const [deleteReason, setDelReason]  = useState('');

  // Editable fields
  const [title,   setTitle]   = useState(event.title || event.name || '');
  const [start,   setStart]   = useState(toLocalDT(event.start));
  const [end,     setEnd]     = useState(toLocalDT(event.end));
  const [status,  setStatus]  = useState(event.status || '');
  const [branch,  setBranch]  = useState(event.branch || '');
  const [therapist, setTherapist] = useState(event.username || '');
  const [comments, setComments]   = useState(event.sessionComments || '');
  const [isOnline, setIsOnline]   = useState(!!event.isOnline);
  const [meetLink, setMeetLink]   = useState(event.meetingLink || '');

  const inp = { width: '100%', padding: '8px 11px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' };
  const lbl = { fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 3 };
  const statusColor = { Completed:'#059669', scheduled:'#2563eb', inprogress:'#d97706', pending:'#92400e', cancelled:'#dc2626' };
  const sc = statusColor[status] || '#6b7280';

  const tc = event.type === 'Leave' ? '#ef4444' : event.type === 'Task' ? '#f59e0b' : event.type === 'FlowTask' ? '#6366f1' : '#2563eb';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: 520, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,0.3)' }}>
        {/* Header strip */}
        <div style={{ background: `linear-gradient(135deg, ${tc}, ${tc}cc)`, padding: '16px 20px', borderRadius: '18px 18px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 900, fontSize: 15, marginBottom: 2 }}>{event.title || event.name || 'Appointment'}</div>
            <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11 }}>
              {event.type || 'Appointment'} · {event.start ? new Date(event.start).toLocaleString('en-IN', { weekday:'short', day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '—'}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {event.patientCard && (
              <span style={{ fontFamily: 'monospace', fontSize: 11, background: 'rgba(255,255,255,0.2)', color: '#fff', borderRadius: 6, padding: '3px 8px', fontWeight: 600 }}>{event.patientCard}</span>
            )}
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontSize: 15 }}>✕</button>
          </div>
        </div>

        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* View mode — clean summary */}
          {!editMode && !confirmDel && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[
                  ['Patient',   event.name || event.patientCard || '—'],
                  ['Therapist', event.leadTherapist
                    ? (event.leadTherapist !== event.username
                        ? <span>{event.leadTherapist} <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 400 }}>(scheduled: {event.username})</span></span>
                        : event.leadTherapist)
                    : (event.username || '—')],
                  ['Start',     event.start ? new Date(event.start).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—'],
                  ['End',       event.end   ? new Date(event.end  ).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '—'],
                  ['Branch',    event.branch || '—'],
                  ['Status',    <span style={{ color: sc, fontWeight: 700 }}>{event.status || '—'}</span>],
                  ['Type',      event.type || '—'],
                  ['Mode',      event.isOnline ? '🌐 Online' : '📍 At Centre'],
                ].map(([k,v]) => (
                  <div key={k} style={{ background: '#f8fafc', borderRadius: 8, padding: '9px 12px' }}>
                    <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, marginBottom: 2 }}>{k}</div>
                    <div style={{ fontSize: 13, color: '#1e3a5f', fontWeight: 600 }}>{v}</div>
                  </div>
                ))}
              </div>
              {event.meetingLink && (
                <div style={{ background: '#f0fdf4', borderRadius: 8, padding: '9px 12px' }}>
                  <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, marginBottom: 2 }}>Meeting Link</div>
                  <a href={event.meetingLink} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#059669', fontWeight: 600, wordBreak: 'break-all' }}>{event.meetingLink}</a>
                </div>
              )}
              {event.sessionComments && (
                <div style={{ background: '#f8fafc', borderRadius: 8, padding: '9px 12px', fontSize: 12, color: '#374151' }}>
                  💬 {event.sessionComments}
                </div>
              )}
              {/* Escalation strip */}
              {event.escalated && (
                <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>
                  🔔 Escalated on {event.escalationDate ? new Date(event.escalationDate).toLocaleDateString('en-IN') : '—'}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                <button onClick={() => setEditMode(true)}
                  style={{ flex: 2, minWidth: 80, padding: '10px 0', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                  ✏️ Edit
                </button>
                {event.patientCard && (
                  <button onClick={() => onEscalate?.(event)}
                    style={{ flex: 2, minWidth: 80, padding: '10px 0', background: event.escalated ? '#fef2f2' : '#fff7ed', color: event.escalated ? '#b91c1c' : '#92400e', border: `1px solid ${event.escalated ? '#fca5a5' : '#fed7aa'}`, borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                    🔔 {event.escalated ? 'View Escalation' : 'Escalate'}
                  </button>
                )}
                <button onClick={() => setConfirmDel(true)}
                  style={{ flex: 1, minWidth: 60, padding: '10px 0', background: '#fef2f2', color: '#ef4444', border: '1px solid #fca5a5', borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                  🗑
                </button>
                <button onClick={onClose}
                  style={{ flex: 1, minWidth: 60, padding: '10px 0', background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                  Close
                </button>
              </div>
            </>
          )}

          {/* Edit mode */}
          {editMode && !confirmDel && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={lbl}>Title</label>
                  <input value={title} onChange={e => setTitle(e.target.value)} style={inp} />
                </div>
                <div><label style={lbl}>Start Date</label>
                  <input type="date" value={start.slice(0,10)} onChange={e => setStart(e.target.value + 'T' + (start.slice(11) || '09:00'))} style={inp} /></div>
                <div><label style={lbl}>Start Time</label>
                  <input type="time" value={start.slice(11,16)} onChange={e => setStart(start.slice(0,10) + 'T' + e.target.value)} style={inp} /></div>
                <div><label style={lbl}>End Date</label>
                  <input type="date" value={end.slice(0,10)} onChange={e => setEnd(e.target.value + 'T' + (end.slice(11) || '10:00'))} style={inp} /></div>
                <div><label style={lbl}>End Time</label>
                  <input type="time" value={end.slice(11,16)} onChange={e => setEnd(end.slice(0,10) + 'T' + e.target.value)} style={inp} /></div>
                <div><label style={lbl}>Therapist</label>
                  <input value={therapist} onChange={e => setTherapist(e.target.value)} style={inp} /></div>
                <div><label style={lbl}>Branch</label>
                  <input value={branch} onChange={e => setBranch(e.target.value)} style={inp} /></div>
                <div><label style={lbl}>Status</label>
                  <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...inp, width: 'auto' }}>
                    {['scheduled','inprogress','completed','pending','cancelled','skipped'].map(s => <option key={s}>{s}</option>)}
                  </select></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={lbl}>Online</label>
                  <div style={{ display: 'flex', gap: 0, borderRadius: 6, overflow: 'hidden', border: '1px solid #d1d5db' }}>
                    <button onClick={() => setIsOnline(false)} style={{ padding: '5px 12px', fontSize: 11, fontWeight: 700, border: 'none', cursor: 'pointer', background: !isOnline ? '#2563eb' : '#f9fafb', color: !isOnline ? '#fff' : '#374151' }}>No</button>
                    <button onClick={() => setIsOnline(true)}  style={{ padding: '5px 12px', fontSize: 11, fontWeight: 700, border: 'none', cursor: 'pointer', background:  isOnline ? '#059669' : '#f9fafb', color:  isOnline ? '#fff' : '#374151' }}>Yes</button>
                  </div>
                </div>
                {isOnline && (
                  <div style={{ gridColumn: '1/-1' }}>
                    <label style={lbl}>Meeting Link</label>
                    <input value={meetLink} onChange={e => setMeetLink(e.target.value)} placeholder="https://meet.google.com/… (optional)" style={inp} />
                  </div>
                )}
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={lbl}>Comments / Notes</label>
                  <textarea value={comments} onChange={e => setComments(e.target.value)} rows={2} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => onSave({ title, start: start ? new Date(start) : undefined, end: end ? new Date(end) : undefined, username: therapist, branch, status, isOnline, meetingLink: meetLink, sessionComments: comments })}
                  style={{ flex: 2, padding: '10px 0', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                  ✓ Save
                </button>
                <button onClick={() => setEditMode(false)}
                  style={{ flex: 1, padding: '10px 0', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 13, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </>
          )}

          {/* Delete confirm */}
          {confirmDel && (
            <div style={{ background: '#fef2f2', borderRadius: 10, padding: '16px 18px' }}>
              <div style={{ fontWeight: 700, color: '#991b1b', marginBottom: 10 }}>Confirm Deletion</div>
              <label style={{ ...lbl, color: '#991b1b' }}>Reason *</label>
              <input value={deleteReason} onChange={e => setDelReason(e.target.value)} placeholder="Enter reason for deletion…"
                style={{ ...inp, borderColor: '#fca5a5', marginBottom: 12 }} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { if (!deleteReason.trim()) { alert('Reason required'); return; } onDelete(deleteReason); }}
                  style={{ flex: 1, padding: '9px 0', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
                  Confirm Delete
                </button>
                <button onClick={() => setConfirmDel(false)}
                  style={{ flex: 1, padding: '9px 0', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
